/**
 * Remove order and show properties from menu item object.
 *
 * @param {object} menuItem - Menu item object
 * @returns {object} menuItemObj Menu item object without properties
 */
export function cleanMenuItem(menuItem) {
  const { order, show, ...menuItemObj } = menuItem;
  return menuItemObj;
}

/**
 * Filter items by `show` property.
 *
 * @param {{
 *  show: boolean
 * }} menuItemObj
 * @returns {boolean} true if menuItemObj.show is not false
 */
export function filterByShow(menuItemObj) {
  return menuItemObj.show !== false;
}

/**
 * Filter items by not empty.
 *
 * @param {object} menuItemObj
 * @returns {boolean} true if item is not empty
 */
export function filterByNotEmpty(menuItemObj) {
  return Object.keys(menuItemObj).length !== 0;
}

/**
 * Sort items by `order` property
 *
 * @param {{
 *  order: number
 * }} a menuItemObj
 * @param {{
 *  order: number
 * }} b menuItemObj
 * @returns {number} -1, 1
 */
export function sortByOrder(a, b) {
  const orderA = a.order || 0;
  const orderB = b.order || 0;
  return orderA < orderB ? -1 : 1;
}

/**
 * Process menu item object.
 * - Remove items with `show` property set to false
 * - Sort items by `order` property
 * - Recursively process links
 * - Remove ids
 *
 * @param {{
 *  links: Array
 *  order: number
 *  show: boolean
 * }} props
 * @returns {object} menuItemObj Menu item object
 */
export function processMenuItemRecursive({ links, ...menuItem }) {
  const cleanedMenuItem = cleanMenuItem(menuItem);

  if (links) {
    cleanedMenuItem.links = links
      .filter(filterByShow)
      .sort(sortByOrder)
      .map((item) => processMenuItemRecursive(item));
  }

  return cleanedMenuItem;
}

/**
 * Process menu items.
 *
 * @param {Array} menuItems Array of menu item objects
 * @returns {Array} Processed menu item objects
 */
function processMenuItems(menuItems) {
  return menuItems
    .filter(filterByShow)
    .sort(sortByOrder)
    .map((menuItem) => processMenuItemRecursive(menuItem));
}

/**
 * Processes a menu item object or an array of menu item objects.
 *
 * @param {Array|object} menu - A menu item object or an array of menu item objects to process.
 * @returns {Array|object} - An array of menu item objects or a menu item object.
 */
export function processMenu(menu) {
  const menuItems = Array.isArray(menu) ? menu : [menu];
  const processedMenuItems = processMenuItems(menuItems);
  return Array.isArray(menu) ? processedMenuItems : (processedMenuItems[0] ?? {});
}
